import React, { useEffect, useState } from 'react'

import Box from 'components_new/atoms/Box'
import Chip from 'components_new/atoms/Chip'
import Drawer from 'components_new/atoms/Drawer'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import LoadingSpinner from 'components_new/atoms/LoadingSpinner'
import Text from 'components_new/atoms/Text'

import { useSIMTableActions } from 'redux/actions/DataPlatform/tables/v4'

type CopiedFrom = 'test' | 'production'

interface Version {
  timestamp: string
  modified: string | null
  name: string | null
  copied_from: CopiedFrom | null
}

interface VersionHistoryProps {
  open: boolean
  onClose: () => void
  tableId: string
  onSelectVersion: (timestamp: string) => void
  activeTimestamp: string | null
}

const formatDate = (modified: string | null): string => {
  if (!modified) return '–'

  return new Date(modified).toLocaleString('sv-SE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

const formatTime = (modified: string | null): string => {
  if (!modified) return ''

  return new Date(modified).toLocaleString('sv-SE', {
    hour: '2-digit',
    minute: '2-digit'
  })
}

const copiedFromLabel = (copiedFrom: CopiedFrom): string =>
  copiedFrom === 'test' ? 'Från testmiljö' : 'Från produktion'

const VersionHistory = (props: VersionHistoryProps) => {
  const { open, onClose, tableId, onSelectVersion, activeTimestamp } = props

  const { tryGetVersions } = useSIMTableActions()

  const [versions, setVersions] = useState<Version[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open) return

    setLoading(true)

    tryGetVersions(tableId)
      .then((data) => {
        setVersions(data)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [open, tableId])

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box
        sx={{
          width: 300,
          display: 'flex',
          flexDirection: 'column',
          height: '100%'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Icon name="History" fontSize="small" color="inherit" />
            <Text variant="subtitle1" fontWeight="700">
              Versionshistorik
            </Text>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Icon name="Close" fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <LoadingSpinner />
            </Box>
          ) : versions.length === 0 ? (
            <Box sx={{ px: 2, py: 3 }}>
              <Text variant="body2" color="text.secondary">
                Inga sparade versioner.
              </Text>
            </Box>
          ) : (
            <Box sx={{ pl: 2, pt: 1 }}>
              {versions.map((version, index) => {
                const isLatest = index === 0
                const isActive = version.timestamp === activeTimestamp

                return (
                  <Box
                    key={version.timestamp}
                    sx={{ display: 'flex', gap: 1.5 }}
                  >
                    {/* Timeline spine */}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        pt: 1.5
                      }}
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          flexShrink: 0,
                          bgcolor: isLatest
                            ? 'success.main'
                            : 'action.disabled',
                          border: isActive ? '2px solid' : 'none',
                          borderColor: 'primary.main'
                        }}
                      />
                      {index < versions.length - 1 && (
                        <Box
                          sx={{
                            width: '2px',
                            flex: 1,
                            minHeight: 24,
                            bgcolor: 'divider',
                            my: 0.5
                          }}
                        />
                      )}
                    </Box>

                    {/* Content */}
                    <Box
                      onClick={() => onSelectVersion(version.timestamp)}
                      sx={{
                        flex: 1,
                        py: 1,
                        pr: 2,
                        cursor: 'pointer',
                        borderRadius: 1,
                        bgcolor: isActive ? 'action.selected' : 'transparent',
                        '&:hover': { bgcolor: 'action.hover' },
                        transition: 'background-color 0.15s'
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 1,
                          mb: 0.25
                        }}
                      >
                        <Text variant="body2" fontWeight="700">
                          {formatDate(version.modified)}
                        </Text>
                        {isLatest && (
                          <Chip label="Aktiv" color="success" size="xsmall" />
                        )}
                        {version.copied_from && (
                          <Chip
                            label={copiedFromLabel(version.copied_from)}
                            color="info"
                            variant="outlined"
                            size="xsmall"
                          />
                        )}
                      </Box>

                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <Icon
                          name="TimerOutlined"
                          fontSize="inherit"
                          color="disabled"
                          sx={{ fontSize: 11 }}
                        />
                        <Text variant="caption" color="text.secondary">
                          {formatTime(version.modified)}
                        </Text>
                      </Box>

                      {version.name && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            mt: 0.25
                          }}
                        >
                          <Icon
                            name="PersonOutlineOutlined"
                            fontSize="inherit"
                            color="disabled"
                            sx={{ fontSize: 11 }}
                          />
                          <Text variant="caption" color="text.secondary">
                            {version.name}
                          </Text>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )
              })}
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  )
}

export default VersionHistory
